import { fromStore, type Readable, type Writable } from 'svelte/store'

export type WritableWithCurrent<T> = {
  set: (value: T) => void
  update: (updater: (value: T) => T) => void
  subscribe: (run: (value: T) => void) => () => void
  current: T
}

export type ReadableWithCurrent<T> = {
  subscribe: (run: (value: T) => void) => () => void
  readonly current: T
}

const isWritable = <T>(
  store: Writable<T> | Readable<T>,
): store is Writable<T> => 'set' in store

export const addCurrent = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Store extends Readable<any>,
  T = Store extends Readable<infer T> ? T : never,
>(
  store: Store,
): Store extends Writable<T>
  ? WritableWithCurrent<T>
  : ReadableWithCurrent<T> => {
  const state = fromStore(store)

  if (isWritable(store)) {
    return {
      set: store.set,
      update: store.update,
      subscribe: store.subscribe,
      get current() {
        return state.current
      },
      set current(value) {
        ;(state as { current: T }).current = value
      },
    } as WritableWithCurrent<T>
  } else {
    return {
      subscribe: store.subscribe,
      get current() {
        return state.current
      },
    } as Store extends Writable<T> ? never : ReadableWithCurrent<T>
  }
}
